import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

export const STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  FINISHED: "finished",
  SKIPPED: "skipped",
} as const;

export const EVENTS = {
  STEP_BEFORE: "step:before",
  STEP_AFTER: "step:after",
  TARGET_NOT_FOUND: "target:notFound",
  TOUR_START: "tour:start",
  TOUR_END: "tour:end",
} as const;

export type StatusKey = typeof STATUS[keyof typeof STATUS];
export type EventKey = typeof EVENTS[keyof typeof EVENTS];

export type Placement = "top" | "bottom" | "left" | "right" | "center";

export interface Step {
  target: string;
  content: ReactNode;
  title?: string;
  placement?: Placement;
}

export interface CallBackProps {
  action?: "start" | "next" | "prev" | "close" | "skip";
  index: number;
  status: StatusKey;
  type: EventKey;
  step?: Step;
}

interface JoyrideProps {
  steps: Step[];
  run: boolean;
  stepIndex: number;
  callback?: (data: CallBackProps) => void;
  continuous?: boolean;
  showSkipButton?: boolean;
  disableOverlayClose?: boolean;
  scrollToFirstStep?: boolean;
}

const overlayStyle = "fixed inset-0 bg-black/40 z-50";
const tooltipBaseStyle =
  "fixed z-50 max-w-sm rounded-lg shadow-lg bg-white text-gray-900 border border-gray-200 p-4 w-[280px]";

const calculateTooltipPosition = (
  rect: DOMRect,
  placement: Placement,
  tooltipRect?: DOMRect | null
): { top: number; left: number; transform: string } => {
  const defaultTransform = "translate(-50%, 0)";
  if (!tooltipRect) {
    return {
      top: rect.bottom + 12 + window.scrollY,
      left: rect.left + rect.width / 2 + window.scrollX,
      transform: defaultTransform,
    };
  }

  switch (placement) {
    case "top":
      return {
        top: rect.top - tooltipRect.height - 12 + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX,
        transform: defaultTransform,
      };
    case "left":
      return {
        top: rect.top + rect.height / 2 + window.scrollY,
        left: rect.left - tooltipRect.width - 12 + window.scrollX,
        transform: "translate(0, -50%)",
      };
    case "right":
      return {
        top: rect.top + rect.height / 2 + window.scrollY,
        left: rect.right + 12 + window.scrollX,
        transform: "translate(0, -50%)",
      };
    case "center":
      return {
        top: rect.top + rect.height / 2 + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX,
        transform: "translate(-50%, -50%)",
      };
    case "bottom":
    default:
      return {
        top: rect.bottom + 12 + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX,
        transform: defaultTransform,
      };
  }
};

export function Joyride({
  steps,
  run,
  stepIndex,
  callback,
  continuous = true,
  showSkipButton = true,
  disableOverlayClose = false,
  scrollToFirstStep = true,
}: JoyrideProps) {
  const currentStep = steps[stepIndex];
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
  const [lastStartIndex, setLastStartIndex] = useState<number | null>(null);

  const placement = useMemo<Placement>(() => currentStep?.placement || "bottom", [currentStep?.placement]);

  const emitCallback = useCallback(
    (data: CallBackProps) => {
      if (callback) {
        callback(data);
      }
    },
    [callback]
  );

  const updateRects = useCallback(() => {
    if (!run || !currentStep) return;
    const targetElement = document.querySelector(currentStep.target) as HTMLElement | null;

    if (!targetElement) {
      // Target not found - just hide tooltip but keep tour running
      setTargetRect(null);
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    setTargetRect(rect);

    if (scrollToFirstStep && stepIndex === 0) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (lastStartIndex !== stepIndex) {
      emitCallback({
        action: "start",
        index: stepIndex,
        status: STATUS.RUNNING,
        type: EVENTS.STEP_BEFORE,
        step: currentStep,
      });
      setLastStartIndex(stepIndex);
    }
  }, [run, currentStep, emitCallback, scrollToFirstStep, stepIndex, lastStartIndex]);

  useEffect(() => {
    if (!run || !currentStep) return;

    updateRects();

    const observer = new ResizeObserver(updateRects);
    const targetElement = document.querySelector(currentStep.target) as HTMLElement | null;
    if (targetElement) {
      observer.observe(targetElement);
    }

    window.addEventListener("resize", updateRects);
    window.addEventListener("scroll", updateRects, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateRects);
      window.removeEventListener("scroll", updateRects, true);
    };
  }, [run, currentStep, updateRects]);

  useEffect(() => {
    if (!run) {
      setTargetRect(null);
    }
  }, [run]);

  useEffect(() => {
    if (tooltipRef.current) {
      setTooltipRect(tooltipRef.current.getBoundingClientRect());
    }
  }, [targetRect, placement, currentStep]);

  const handleNext = () => {
    if (stepIndex >= steps.length - 1) {
      emitCallback({
        action: "next",
        index: stepIndex,
        status: STATUS.FINISHED,
        type: EVENTS.TOUR_END,
        step: currentStep,
      });
      return;
    }

    emitCallback({
      action: "next",
      index: stepIndex,
      status: STATUS.RUNNING,
      type: EVENTS.STEP_AFTER,
      step: currentStep,
    });
  };

  const handlePrev = () => {
    emitCallback({
      action: "prev",
      index: stepIndex,
      status: STATUS.RUNNING,
      type: EVENTS.STEP_AFTER,
      step: currentStep,
    });
  };

  const handleSkip = () => {
    emitCallback({
      action: "skip",
      index: stepIndex,
      status: STATUS.SKIPPED,
      type: EVENTS.TOUR_END,
      step: currentStep,
    });
  };

  const handleClose = () => {
    emitCallback({
      action: "close",
      index: stepIndex,
      status: STATUS.FINISHED,
      type: EVENTS.TOUR_END,
      step: currentStep,
    });
  };

  if (!run || !currentStep || !targetRect) {
    return null;
  }

  const tooltipPosition = calculateTooltipPosition(targetRect, placement, tooltipRect);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;

  return (
    <>
      <div className={overlayStyle} onClick={disableOverlayClose ? undefined : handleClose} />
      <div
        ref={tooltipRef}
        className={tooltipBaseStyle}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: tooltipPosition.transform,
        }}
      >
        {currentStep.title && <h3 className="text-base font-semibold mb-1">{currentStep.title}</h3>}
        <div className="text-sm text-gray-700">{currentStep.content}</div>

        <div className="flex items-center justify-between mt-4 gap-2">
          <div className="flex items-center gap-2">
            {showSkipButton && (
              <button className="text-xs text-muted-foreground hover:text-gray-900" onClick={handleSkip}>
                Skip tour
              </button>
            )}
            {!isFirstStep && (
              <button className="text-xs px-3 py-1 border rounded" onClick={handlePrev}>
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">
              Step {stepIndex + 1} of {steps.length}
            </span>
            <button
              className="text-xs px-3 py-1 bg-primary text-primary-foreground rounded"
              onClick={isLastStep || !continuous ? handleClose : handleNext}
            >
              {isLastStep || !continuous ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default Joyride;
